import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { getSiteOrigin, getOgImagePath, getOgImageDimensions } from "../../config/site";
import { WA_LINK, TG_LINK } from "../../config/contactLinks";

/**
 * Уникальный title/description, canonical, Open Graph, Twitter, keywords, JSON-LD.
 */
function PageSeo({ title, description, faqItems, breadcrumbItems }) {
  const { lang, t } = useLanguage();
  const location = useLocation();
  const origin = getSiteOrigin();

  const canonicalUrl = useMemo(() => {
    try {
      return new URL(location.pathname, `${origin}/`).href;
    } catch {
      return origin;
    }
  }, [location.pathname, origin]);

  const ogImagePath = getOgImagePath();
  const ogImage = `${origin}${ogImagePath}`;
  const { width: ogW, height: ogH } = getOgImageDimensions();
  const keywords = t("seo.keywords");
  const siteName = t("seo.siteName");
  const ogImageAltRaw = t("seo.ogImageAlt");
  const ogImageAlt =
    typeof ogImageAltRaw === "string" && ogImageAltRaw !== "seo.ogImageAlt"
      ? ogImageAltRaw
      : siteName;
  const orgDescription = t("seo.home.description");
  const ogLocale = { az: "az_AZ", ru: "ru_RU", en: "en_US" }[lang] || "en_US";

  const jsonLd = useMemo(() => {
    const graph = [
      {
        "@type": "Organization",
        "@id": `${origin}/#organization`,
        name: siteName,
        url: origin,
        logo: { "@type": "ImageObject", url: `${origin}/logo/big-logo.png`, width: 3000, height: 3000 },
        image: ogImage,
        description: orgDescription,
        sameAs: [TG_LINK, WA_LINK],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "sales",
          availableLanguage: ["Azerbaijani", "Russian", "English"],
        },
      },
      {
        "@type": "ProfessionalService",
        "@id": `${origin}/#service`,
        name: siteName,
        url: origin,
        image: ogImage,
        description: orgDescription,
        provider: { "@id": `${origin}/#organization` },
        areaServed: { "@type": "Country", name: "Azerbaijan" },
      },
      {
        "@type": "WebSite",
        "@id": `${origin}/#website`,
        name: siteName,
        url: origin,
        publisher: { "@id": `${origin}/#organization` },
        inLanguage: [ogLocale, "az", "ru", "en"],
      },
      {
        "@type": "WebPage",
        "@id": canonicalUrl,
        url: canonicalUrl,
        name: title,
        description,
        isPartOf: { "@id": `${origin}/#website` },
        inLanguage: lang,
      },
    ];
    if (Array.isArray(breadcrumbItems) && breadcrumbItems.length > 1) {
      try {
        const items = breadcrumbItems.map((crumb, i) => {
          const itemUrl = new URL(crumb.path, `${origin}/`).href;
          return {
            "@type": "ListItem",
            position: i + 1,
            name: crumb.name,
            item: itemUrl,
          };
        });
        graph.push({
          "@type": "BreadcrumbList",
          "@id": `${canonicalUrl}#breadcrumb`,
          itemListElement: items,
        });
      } catch {
        /* ignore */
      }
    }
    if (Array.isArray(faqItems) && faqItems.length > 0) {
      graph.push({
        "@type": "FAQPage",
        "@id": `${canonicalUrl}#faq`,
        isPartOf: { "@id": `${origin}/#website` },
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.a,
          },
        })),
      });
    }
    return { "@context": "https://schema.org", "@graph": graph };
  }, [
    breadcrumbItems,
    canonicalUrl,
    description,
    faqItems,
    lang,
    ogImage,
    ogLocale,
    orgDescription,
    origin,
    siteName,
    title,
  ]);

  const ogLocaleAlternates = useMemo(
    () => ["az_AZ", "ru_RU", "en_US"].filter((l) => l !== ogLocale),
    [ogLocale]
  );
  const isHttps = canonicalUrl.startsWith("https://");

  return (
    <Helmet htmlAttributes={{ lang }} prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="application-name" content={siteName} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      {isHttps ? <meta property="og:image:secure_url" content={ogImage} /> : null}
      <meta property="og:image:width" content={String(ogW)} />
      <meta property="og:image:height" content={String(ogH)} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:locale" content={ogLocale} />
      {ogLocaleAlternates.map((loc) => (
        <meta key={loc} property="og:locale:alternate" content={loc} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}

export default PageSeo;
